import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Axios from "axios";
import { useParams } from "react-router-dom";
import { Button, Card, Image, Loader, Message, Row } from "components/lib";
import { applyFormTheme, useFormBrandColor } from "components/survey/formTheme";
import { SanctionsRecordCard } from "./sanctions-results";
import { StylesManager, Model } from "survey-core";
import { PopupSurvey, Survey } from "survey-react-ui";
import "survey-core/defaultV2.min.css";
import { attachScoring } from "./scoringRunner";
import { ScoringSummary } from "./scoringSummary";

const Settings = require("settings.json");
const serverUrl = Settings[process.env.NODE_ENV].server_url;
const RESULTS_PAGE_SIZE = 5;

StylesManager.applyTheme("defaultV2");

function buildSurveyModel(schema, options = {}) {
  if (!schema || typeof schema !== "object") return null;

  const model = new Model(schema);
  model.showCompletedPage = false;
  applyFormTheme(model, options.theme);

  if (options.title && !model.title) {
    model.title = options.title;
  }

  if (options.mode) {
    model.mode = options.mode;
  }

  if (options.data && typeof options.data === "object") {
    model.data = options.data;
  }

  return model;
}

function formatSubmittedAnswers(sender, uploadPrefix) {
  const output = {};

  Object.keys(sender?.data || {}).forEach((key) => {
    const question = sender.getQuestionByName(key);
    if (!question) return;

    if (question.getType() === "file" && Array.isArray(question.value)) {
      output[key] = question.value.map((fileItem) => ({
        ...fileItem,
        key: fileItem.key || `${uploadPrefix}/${key}/${fileItem.name}`,
      }));
      return;
    }

    output[key] = question.value;
  });

  return output;
}

function getPaginatedItems(items, page, pageSize = RESULTS_PAGE_SIZE) {
  const safeItems = Array.isArray(items) ? items : [];
  const totalPages = Math.max(1, Math.ceil(safeItems.length / pageSize));
  const currentPage = Math.min(Math.max(page, 1), totalPages);
  const startIndex = (currentPage - 1) * pageSize;

  return {
    items: safeItems.slice(startIndex, startIndex + pageSize),
    currentPage,
    totalPages,
    startIndex,
    totalCount: safeItems.length,
  };
}

function PaginationControls({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-4 text-sm text-slate-600">
      <p>
        Page {page} of {totalPages}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          className="rounded-lg border border-slate-300 px-3 py-2 font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          Previous
        </button>
        <button
          type="button"
          className="rounded-lg border border-slate-300 px-3 py-2 font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function getSanctionsResultHtml({ records, totalCount, startIndex }) {
  if (!totalCount) {
    return <p className="text-sm text-slate-700">PEP/Sanctions Results: No matches found</p>;
  }

  return (
    <div className="space-y-4 text-sm text-slate-700">
      <p>
        PEP/Sanctions Results: <strong>Possible Matches found</strong>
      </p>
      <p className="text-xs text-slate-500">
        Showing {startIndex + 1}-{startIndex + records.length} of {totalCount}
      </p>
      {records.map((record, index) => (
        <SanctionsRecordCard
          key={`${record?.id || record?.source_id || "sanctions"}-${index}`}
          record={record}
          defaultExpanded={totalCount === 1}
        />
      ))}
    </div>
  );
}

function getAdverseMediaResults({ results, totalCount, startIndex, hasMatches }) {
  if (!hasMatches || !totalCount) {
    return <p className="text-sm text-slate-700">Adverse Media Results: No matches found</p>;
  }

  return (
    <div className="space-y-4 text-sm text-slate-700">
      <p>
        Adverse Media Results: <strong>Possible Matches found</strong>
      </p>
      <p className="text-xs text-slate-500">
        Showing {startIndex + 1}-{startIndex + results.length} of {totalCount}
      </p>
      {results.map((result, index) => (
        <div
          key={`${result?.url || "adverse"}-${index}`}
          className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
        >
          <p className="text-sky-600">
            <a href={result?.url} target="_blank" rel="noopener noreferrer">
              <strong>{result?.title || "Untitled result"}</strong>
            </a>
          </p>
          <p>Text: {result?.text || "No summary available"}</p>
        </div>
      ))}
    </div>
  );
}

function BiometricImageCarousel({ name, images }) {
  const [index, setIndex] = useState(0);
  const safeIndex = Math.min(index, images.length - 1);
  const current = images[safeIndex];
  const showControls = images.length > 1;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center justify-between pb-2">
        <p className="text-sm font-medium text-slate-700">
          {name || "Verification image"}
        </p>
        {showControls && (
          <p className="text-xs text-slate-500">
            {safeIndex + 1} / {images.length}
          </p>
        )}
      </div>
      {current?.timestamp && (
        <p className="pb-2 text-xs text-slate-500">
          {new Date(current.timestamp).toLocaleString()}
        </p>
      )}
      <div className="relative">
        <Image
          source={`data:${current.mimetype || "image/png"};base64,${current.image}`}
          alt={name || "Verification image"}
          className="w-full rounded-lg border border-slate-200 bg-white object-contain"
        />
        {showControls && (
          <>
            <button
              type="button"
              onClick={() =>
                setIndex(
                  (i) => (i - 1 + images.length) % images.length
                )
              }
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 px-3 py-1 text-base font-medium text-slate-700 shadow"
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => setIndex((i) => (i + 1) % images.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 px-3 py-1 text-base font-medium text-slate-700 shadow"
              aria-label="Next image"
            >
              ›
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children, defaultOpen = false }) {
  return (
    <details
      className="rounded-lg border border-slate-200 bg-white"
      open={defaultOpen}
    >
      <summary className="cursor-pointer px-4 py-3 font-medium text-slate-900">
        {title}
      </summary>
      <div className="border-t border-slate-200 px-4 py-4">{children}</div>
    </details>
  );
}

function getStoredSessionToken(verificationId) {
  return window.sessionStorage.getItem(
    `public-risk-assessment-session:${verificationId}`
  );
}

function storeSessionToken(verificationId, token) {
  window.sessionStorage.setItem(
    `public-risk-assessment-session:${verificationId}`,
    token
  );
}

function clearSessionToken(verificationId) {
  window.sessionStorage.removeItem(
    `public-risk-assessment-session:${verificationId}`
  );
}

export function PublicRiskAssessmentView() {
  const params = useParams();
  const queryToken = useMemo(
    () => new URLSearchParams(window.location.search).get("token"),
    []
  );
  const [bootLoading, setBootLoading] = useState(true);
  const [contextLoading, setContextLoading] = useState(false);
  const [pinSubmitting, setPinSubmitting] = useState(false);
  const [assessmentSubmitting, setAssessmentSubmitting] = useState(false);
  const [biometricImagesLoading, setBiometricImagesLoading] = useState(false);
  const [bootData, setBootData] = useState(null);
  const [contextData, setContextData] = useState(null);
  const [biometricImages, setBiometricImages] = useState([]);
  const [hydratedIntakeAnswers, setHydratedIntakeAnswers] = useState(null);
  const [sessionToken, setSessionToken] = useState(() =>
    getStoredSessionToken(params.id)
  );
  const [pin, setPin] = useState("");
  const [error, setError] = useState(null);
  const [pinError, setPinError] = useState(null);
  const [pinSuccess, setPinSuccess] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [biometricImageError, setBiometricImageError] = useState(null);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [sanctionsPage, setSanctionsPage] = useState(1);
  const [adverseMediaPage, setAdverseMediaPage] = useState(1);
  const isSubmittingRef = useRef(false);

  const lockReviewSession = useCallback(
    (nextError = null) => {
      clearSessionToken(params.id);
      setSessionToken(null);
      setContextData(null);
      setBiometricImages([]);
      setBiometricImageError(null);
      setPinSuccess(null);
      setPinError(null);
      setSubmitError(null);
      setSurveyOpen(false);
      setError(nextError);
    },
    [params.id]
  );

  useEffect(() => {
    let mounted = true;

    async function loadBootState() {
      if (!queryToken) {
        if (mounted) {
          setError("Public risk assessment token is missing.");
          setBootLoading(false);
        }
        return;
      }

      setBootLoading(true);
      setError(null);

      try {
        const response = await Axios.get(
          `/api/public/risk-assessment/v1/${params.id}`,
          { params: { token: queryToken } }
        );
        if (!mounted) return;
        setBootData(response.data?.data || null);
      } catch (err) {
        if (!mounted) return;
        setBootData(null);
        setError(
          err?.response?.data?.message ||
            "Unable to load this public risk assessment link."
        );
      } finally {
        if (mounted) {
          setBootLoading(false);
        }
      }
    }

    loadBootState();

    return () => {
      mounted = false;
    };
  }, [params.id, queryToken]);

  useEffect(() => {
    let mounted = true;

    async function loadContext() {
      if (!sessionToken || !queryToken) return;

      setContextLoading(true);
      setError(null);

      try {
        const response = await Axios.get(
          `/api/public/risk-assessment/v1/${params.id}/context`,
          {
            headers: {
              Authorization: `Bearer ${sessionToken}`,
            },
          }
        );

        if (!mounted) return;
        setContextData(response.data?.data || null);
      } catch (err) {
        if (!mounted) return;
        setContextData(null);

        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          lockReviewSession(
            err?.response?.data?.message ||
              "Your secure review session expired. Enter your PIN again."
          );
          return;
        }

        setError(
          err?.response?.data?.message ||
            "Unable to load the risk assessment review context."
        );
      } finally {
        if (mounted) {
          setContextLoading(false);
        }
      }
    }

    loadContext();

    return () => {
      mounted = false;
    };
  }, [lockReviewSession, params.id, queryToken, sessionToken]);

  useEffect(() => {
    let mounted = true;

    async function loadBiometricImages() {
      if (!sessionToken || !Array.isArray(contextData?.biometric_images)) {
        if (mounted) {
          setBiometricImages([]);
          setBiometricImageError(null);
          setBiometricImagesLoading(false);
        }
        return;
      }

      if (contextData.biometric_images.length === 0) {
        if (mounted) {
          setBiometricImages([]);
          setBiometricImageError(null);
          setBiometricImagesLoading(false);
        }
        return;
      }

      setBiometricImagesLoading(true);
      setBiometricImageError(null);

      const results = await Promise.allSettled(
        contextData.biometric_images.map(async (image) => {
          const response = await Axios.get(
            `/api/public/risk-assessment/v1/${params.id}/image/${image.id}`,
            {
              headers: {
                Authorization: `Bearer ${sessionToken}`,
              },
            }
          );

          return {
            ...image,
            ...response.data?.data,
          };
        })
      );

      if (!mounted) return;

      const authFailure = results.find(
        (result) =>
          result.status === "rejected" &&
          [401, 403].includes(result.reason?.response?.status)
      );

      if (authFailure) {
        lockReviewSession(
          authFailure.reason?.response?.data?.message ||
            "Your secure review session expired. Enter your PIN again."
        );
        return;
      }

      const loadedImages = results
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);

      setBiometricImages(loadedImages);

      if (loadedImages.length !== contextData.biometric_images.length) {
        setBiometricImageError(
          "Some biometric images could not be loaded for this review session."
        );
      }

      setBiometricImagesLoading(false);
    }

    loadBiometricImages();

    return () => {
      mounted = false;
    };
  }, [contextData?.biometric_images, lockReviewSession, params.id, sessionToken]);

  useEffect(() => {
    let mounted = true;

    async function loadIntakeFiles() {
      const answers = contextData?.intake?.answers;
      if (!sessionToken || !answers || typeof answers !== "object") {
        if (mounted) setHydratedIntakeAnswers(answers || null);
        return;
      }

      // collect uploaded file items (arrays of objects carrying a GCS key)
      const fileItems = [];
      Object.values(answers).forEach((value) => {
        if (!Array.isArray(value)) return;
        value.forEach((item) => {
          if (item && typeof item === "object" && typeof item.key === "string") {
            fileItems.push(item);
          }
        });
      });

      if (fileItems.length === 0) {
        if (mounted) setHydratedIntakeAnswers(answers);
        return;
      }

      const results = await Promise.allSettled(
        fileItems.map(async (item) => {
          const response = await Axios.get(
            `/api/public/risk-assessment/v1/${params.id}/intake-file`,
            {
              params: { content: item.key, name: item.name },
              headers: {
                Authorization: `Bearer ${sessionToken}`,
              },
            }
          );
          return { key: item.key, blob: response.data?.data?.blob };
        })
      );

      if (!mounted) return;

      const authFailure = results.find(
        (result) =>
          result.status === "rejected" &&
          [401, 403].includes(result.reason?.response?.status)
      );

      if (authFailure) {
        lockReviewSession(
          authFailure.reason?.response?.data?.message ||
            "Your secure review session expired. Enter your PIN again."
        );
        return;
      }

      const blobByKey = new Map();
      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value?.blob) {
          blobByKey.set(result.value.key, result.value.blob);
        }
      });

      // replace each file item's expiring content url with the inlined data url
      const hydrated = {};
      Object.entries(answers).forEach(([field, value]) => {
        hydrated[field] = Array.isArray(value)
          ? value.map((item) =>
              item && typeof item === "object" && blobByKey.has(item.key)
                ? { ...item, content: blobByKey.get(item.key) }
                : item
            )
          : value;
      });

      setHydratedIntakeAnswers(hydrated);
    }

    loadIntakeFiles();

    return () => {
      mounted = false;
    };
  }, [contextData?.intake?.answers, lockReviewSession, params.id, sessionToken]);

  const intakeSurveyModel = useMemo(() => {
    const model = buildSurveyModel(contextData?.intake?.form_schema, {
      title: contextData?.intake?.form_name || "Intake form",
      mode: "display",
      data: hydratedIntakeAnswers || contextData?.intake?.answers || {},
    });

    if (model) {
      // serve the re-fetched, inlined data url; never the expired signed url
      model.onDownloadFile.add((sender, options) => {
        if (
          typeof options.content === "string" &&
          options.content.startsWith("data:")
        ) {
          options.callback("success", options.content);
          return;
        }
        options.callback("success", "");
      });
    }

    return model;
  }, [
    contextData?.intake?.answers,
    contextData?.intake?.form_name,
    contextData?.intake?.form_schema,
    hydratedIntakeAnswers,
  ]);

  const assessmentSurveyModel = useMemo(
    () =>
      buildSurveyModel(contextData?.form?.schema, {
        title: contextData?.form?.name || "Risk Assessment",
        theme: contextData?.form?.theme,
      }),
    [
      contextData?.form?.name,
      contextData?.form?.schema,
      contextData?.form?.theme,
    ]
  );

  // frames the page in the assessment form's colour (R8)
  useFormBrandColor(contextData?.form?.theme);
  const sanctionsRecords = useMemo(
    () =>
      Array.isArray(contextData?.sanctions_results?.found_records)
        ? contextData.sanctions_results.found_records
        : [],
    [contextData?.sanctions_results]
  );
  const adverseMediaSummary = contextData?.adverse_media_results?.AML?.[0] || null;
  const adverseMediaRecords = useMemo(
    () =>
      Array.isArray(adverseMediaSummary?.data) ? adverseMediaSummary.data : [],
    [adverseMediaSummary]
  );
  const paginatedSanctions = useMemo(
    () => getPaginatedItems(sanctionsRecords, sanctionsPage),
    [sanctionsPage, sanctionsRecords]
  );
  const paginatedAdverseMedia = useMemo(
    () => getPaginatedItems(adverseMediaRecords, adverseMediaPage),
    [adverseMediaPage, adverseMediaRecords]
  );
  const groupedBiometricImages = useMemo(() => {
    const groups = new Map();
    biometricImages.forEach((image) => {
      const key = image.name || "Verification image";
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(image);
    });
    return Array.from(groups, ([name, images]) => ({ name, images }));
  }, [biometricImages]);

  const uploadPrefix = contextData?.risk_assessment?.id || params.id;

  useEffect(() => {
    setSanctionsPage(1);
  }, [contextData?.sanctions_results]);

  useEffect(() => {
    setAdverseMediaPage(1);
  }, [contextData?.adverse_media_results]);

  useEffect(() => {
    if (!assessmentSurveyModel) return undefined;
    return attachScoring(assessmentSurveyModel, contextData?.form?.schema);
  }, [assessmentSurveyModel, contextData?.form?.schema]);

  useEffect(() => {
    if (!assessmentSurveyModel || !sessionToken) return undefined;

    const uploadFiles = (sender, options) => {
      const formData = new FormData();
      options.files.forEach((file) => {
        formData.append("file", file);
        formData.append("response_id", uploadPrefix);
        formData.append("field_name", options.name);
        formData.append("file_name", file.name);
      });

      Axios.post(`${serverUrl}/api/utility/gcp/upload`, formData)
        .then((response) => {
          options.callback(
            "success",
            options.files.map((file) => ({
              file,
              name: file.name,
              type: file.type || "application/octet-stream",
              content: response.data.url,
              key: response.data.name,
              id: file.id,
            }))
          );
        })
        .catch(() => {
          options.callback("error", "File upload failed");
        });
    };

    const completeSurvey = async (sender, options) => {
      if (isSubmittingRef.current) return;

      isSubmittingRef.current = true;
      options.allowComplete = false;
      setAssessmentSubmitting(true);
      setSubmitError(null);

      try {
        const answers = formatSubmittedAnswers(sender, uploadPrefix);
        const response = await Axios.post(
          `/api/public/risk-assessment/v1/${params.id}/complete`,
          answers,
          {
            headers: {
              Authorization: `Bearer ${sessionToken}`,
            },
          }
        );

        clearSessionToken(params.id);
        window.location.replace(
          response?.data?.data?.finish_path ||
            `/finish?id=${params.id}&mode=tranche2-risk-assessment`
        );
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          lockReviewSession(
            err?.response?.data?.message ||
              "Your secure review session expired. Enter your PIN again."
          );
        } else {
          setSubmitError(
            err?.response?.data?.message ||
              "Unable to complete the risk assessment right now."
          );
        }
      } finally {
        setAssessmentSubmitting(false);
        isSubmittingRef.current = false;
      }
    };

    assessmentSurveyModel.onUploadFiles.add(uploadFiles);
    assessmentSurveyModel.onCompleting.add(completeSurvey);

    return () => {
      assessmentSurveyModel.onUploadFiles.remove(uploadFiles);
      assessmentSurveyModel.onCompleting.remove(completeSurvey);
    };
  }, [
    assessmentSurveyModel,
    lockReviewSession,
    params.id,
    sessionToken,
    uploadPrefix,
  ]);

  const submitPin = useCallback(
    async (pinValue) => {
      if (!queryToken || pinSubmitting) return;

      const normalizedPin =
        typeof pinValue === "string"
          ? pinValue.replace(/\D/g, "").slice(0, 6)
          : "";

      if (normalizedPin.length !== 6) {
        setPinError("Enter a 6-digit PIN.");
        return;
      }

      setPinSubmitting(true);
      setPinError(null);
      setPinSuccess(null);
      setError(null);

      try {
        const response = await Axios.post(
          `/api/public/risk-assessment/v1/${params.id}/pin`,
          {
            token: queryToken,
            pin: normalizedPin,
          }
        );
        const nextSessionToken = response.data?.data?.token;
        setSessionToken(nextSessionToken);
        storeSessionToken(params.id, nextSessionToken);
        setPin("");
        setSurveyOpen(false);
        setPinSuccess("PIN verified. Review data is now available.");
      } catch (err) {
        const failedPinState = err?.response?.data?.data;

        if (failedPinState) {
          setBootData((current) => {
            if (!current) return current;

            return {
              ...current,
              public_link: {
                ...current.public_link,
                failed_attempt_count:
                  failedPinState.failed_attempt_count ??
                  current?.public_link?.failed_attempt_count ??
                  0,
                attempts_remaining:
                  failedPinState.attempts_remaining ??
                  current?.public_link?.attempts_remaining ??
                  0,
                blocked:
                  failedPinState.blocked ??
                  current?.public_link?.blocked ??
                  false,
              },
            };
          });
        }

        setPinError(
          err?.response?.data?.message ||
            "Unable to verify the risk assessment PIN."
        );
      } finally {
        setPinSubmitting(false);
      }
    },
    [params.id, pinSubmitting, queryToken]
  );

  const verifyPin = async (event) => {
    event.preventDefault();
    await submitPin(pin);
  };

  const handlePinChange = (event) => {
    const nextPin = event.target.value.replace(/\D/g, "").slice(0, 6);
    setPin(nextPin);

    if (nextPin.length === 6 && !pinSubmitting) {
      submitPin(nextPin);
    }
  };

  if (bootLoading || contextLoading || assessmentSubmitting) {
    return <Loader />;
  }

  if (!bootData && error) {
    return (
      <Row
        color="brand"
        title={error}
      />
    );
  }

  const biometricSummary = contextData?.biometric_summary || null;
  const reviewFormReady = !!assessmentSurveyModel && !!sessionToken;

  return (
    <>
      <div className="mx-auto w-[96vw] max-w-[1040px] px-2 sm:px-4">
        <Card title="Tranche 2 Risk Assessment Review">
          {error && <Message type="warning">{error}</Message>}
          {pinError && <Message type="warning">{pinError}</Message>}
          {pinSuccess && <Message type="success">{pinSuccess}</Message>}
          {submitError && <Message type="warning">{submitError}</Message>}

          {!sessionToken && (
            <>
              <Message>
                Enter your 6-digit reviewer PIN to unlock the assessment context
                and complete this tranche 2 review.
              </Message>

              <div className="pb-4 text-sm text-slate-600">
                <p>
                  Link expires:{" "}
                  {bootData?.public_link?.expires_at
                    ? new Date(bootData.public_link.expires_at).toLocaleString()
                    : "Unknown"}
                </p>
                <p>
                  Attempts remaining:{" "}
                  {bootData?.public_link?.attempts_remaining ?? 0}
                </p>
              </div>

              <form className="max-w-sm space-y-4" onSubmit={verifyPin}>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-slate-700">
                    Reviewer PIN
                  </span>
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoComplete="one-time-code"
                    value={pin}
                    onChange={handlePinChange}
                    className="w-full rounded-lg border border-slate-300 px-3 py-3 text-lg tracking-[0.35em] outline-none transition focus:border-slate-500"
                    placeholder="000000"
                  />
                </label>

                <button
                  type="submit"
                  className="rounded-lg bg-slate-900 px-4 py-3 text-sm font-medium text-white"
                  disabled={pinSubmitting}
                >
                  {pinSubmitting ? "Verifying PIN..." : "Verify PIN"}
                </button>
              </form>
            </>
          )}

          {sessionToken && contextData && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 px-4 py-4 text-sm text-slate-700">
                <p>
                  Client: {contextData?.verification?.first_name || "Unknown"}{" "}
                  {contextData?.verification?.last_name || ""}
                </p>
                <p>Email: {contextData?.verification?.email || "No email"}</p>
                <p>
                  Biometric result: {contextData?.verification?.result || "Pending"}
                </p>
                <p>
                  Risk assessment status:{" "}
                  {contextData?.risk_assessment?.status || "Unknown"}
                </p>
              </div>

              {(contextData?.scoring ||
                contextData?.risk_assessment?.scoring) && (
                <ScoringSummary
                  scoring={
                    contextData?.scoring ||
                    contextData?.risk_assessment?.scoring
                  }
                  rating={
                    contextData?.rating || contextData?.risk_assessment?.rating
                  }
                />
              )}

              <Section title="Biometric Check Summary" defaultOpen>
                <div className="grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
                  <p>Result: {biometricSummary?.result || "Unknown"}</p>
                  <p>Reason: {biometricSummary?.reason || "None"}</p>
                  <p>
                    Decision time: {biometricSummary?.decision_time || "Unknown"}
                  </p>
                  <p>
                    Date of birth: {biometricSummary?.date_of_birth || "Unknown"}
                  </p>
                  <p>
                    Document type: {biometricSummary?.document_type || "Unknown"}
                  </p>
                  <p>
                    Document country:{" "}
                    {biometricSummary?.document_country || "Unknown"}
                  </p>
                </div>

                <div className="pt-4">
                  {contextData?.biometric_images?.length > 0 && (
                    <p className="pb-2 text-sm font-medium text-slate-700">
                      Session images
                    </p>
                  )}

                  {biometricImageError && (
                    <Message type="warning">{biometricImageError}</Message>
                  )}

                  {biometricImagesLoading && (
                    <p className="text-sm text-slate-500">
                      Loading biometric images...
                    </p>
                  )}

                  {!biometricImagesLoading &&
                    contextData?.biometric_images?.length === 0 && (
                      <p className="text-sm text-slate-500">
                        No biometric images are available for this verification.
                      </p>
                    )}

                  {groupedBiometricImages.length > 0 && (
                    <div className="grid gap-4 pt-2 md:grid-cols-2">
                      {groupedBiometricImages.map((group) => (
                        <BiometricImageCarousel
                          key={group.name}
                          name={group.name}
                          images={group.images}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </Section>

              <Section title="Intake Form" defaultOpen>
                <div className="space-y-3">
                  <p className="text-sm text-slate-700">
                    {contextData?.intake?.form_name || "Intake form"}
                  </p>
                  {intakeSurveyModel ? (
                    <div className="overflow-x-auto">
                      <Survey model={intakeSurveyModel} />
                    </div>
                  ) : (
                    <Message type="warning">
                      The intake form snapshot is unavailable for this review.
                    </Message>
                  )}
                </div>
              </Section>

              <Section title="PEP/Sanctions">
                {getSanctionsResultHtml({
                  records: paginatedSanctions.items,
                  totalCount: paginatedSanctions.totalCount,
                  startIndex: paginatedSanctions.startIndex,
                })}
                <PaginationControls
                  page={paginatedSanctions.currentPage}
                  totalPages={paginatedSanctions.totalPages}
                  onChange={setSanctionsPage}
                />
              </Section>

              <Section title="Adverse Media">
                {getAdverseMediaResults(
                  {
                    results: paginatedAdverseMedia.items,
                    totalCount: paginatedAdverseMedia.totalCount,
                    startIndex: paginatedAdverseMedia.startIndex,
                    hasMatches:
                      !!adverseMediaSummary &&
                      adverseMediaSummary?.status?.serviceSuspected !== false &&
                      paginatedAdverseMedia.totalCount > 0,
                  }
                )}
                <PaginationControls
                  page={paginatedAdverseMedia.currentPage}
                  totalPages={paginatedAdverseMedia.totalPages}
                  onChange={setAdverseMediaPage}
                />
              </Section>

              <div className="flex flex-wrap gap-3 pt-2">
                {reviewFormReady && !surveyOpen && (
                  <Button
                    text="Open Risk Assessment Form"
                    action={() => setSurveyOpen(true)}
                  />
                )}
                {reviewFormReady && surveyOpen && (
                  <Button
                    text="Hide Risk Assessment Form"
                    action={() => setSurveyOpen(false)}
                  />
                )}
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700"
                  onClick={() => lockReviewSession()}
                >
                  Lock Review Data
                </button>
              </div>

              {!reviewFormReady && (
                <Message type="warning">
                  The configured risk assessment form is unavailable.
                </Message>
              )}
            </div>
          )}
        </Card>
      </div>

      {surveyOpen && reviewFormReady && assessmentSurveyModel && (
        <PopupSurvey
          model={assessmentSurveyModel}
          isExpanded
          allowClose={false}
          allowFullScreen={false}
        />
      )}
    </>
  );
}
